import React, { useState, useRef, useEffect } from 'react';
import { avatarColor } from '../../lib/avatars';
import { useShell } from '../../context/ShellContext';
import './TopBar.css';

export default function TopBar({
  user,
  profile,
  displayName,
  onSignOut,
  showDetailToggle,
  hasUnreadNav,
  showSubChatToggle,
  hasUnreadSubChats,
  selectedRootChat,
  isChannelOwner,
  onOpenChannelSettings,
}) {
  const { isNavPinned, openNav, isDetailPinned, detailPaneOpen, toggleDetailPane, openSubChatsPane } = useShell();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [channelMenuOpen, setChannelMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const channelMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
      if (channelMenuRef.current && !channelMenuRef.current.contains(e.target)) {
        setChannelMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setChannelMenuOpen(false);
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <header className="top-nav">
      <div className="nav-left-group">
        {!isNavPinned && (
          <button
            className="topbar-icon-btn topbar-icon-btn--indicator-wrap"
            onClick={openNav}
            aria-label="Open navigation"
            title="Open navigation"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2 4h14M2 9h14M2 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            {hasUnreadNav && <span className="topbar-unread-dot" aria-hidden="true" />}
          </button>
        )}
        <div className="nav-brand">
          <span className="nav-brand-logo">✦</span>
          <span className="nav-brand-name">ZAPEN</span>
        </div>

        {selectedRootChat && (
          <div className="channel-menu-wrap" ref={channelMenuRef}>
            <button
              className="channel-trigger"
              onClick={() => setChannelMenuOpen(o => !o)}
              aria-haspopup="true"
              aria-expanded={channelMenuOpen}
            >
              <span className="channel-trigger-name">{selectedRootChat.name}</span>
              <span className="channel-trigger-caret" aria-hidden="true">
                {channelMenuOpen ? '▲' : '▼'}
              </span>
            </button>

            {channelMenuOpen && (
              <div className="channel-dropdown" role="menu">
                {isChannelOwner && (
                  <button
                    className="channel-dropdown-item"
                    role="menuitem"
                    onClick={() => { setChannelMenuOpen(false); onOpenChannelSettings?.(); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.3"/>
                      <path d="M7 1v1M7 12v1M1 7h1M12 7h1M2.6 2.6l.7.7M10.7 10.7l.7.7M2.6 11.4l.7-.7M10.7 3.3l.7-.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                    Settings
                  </button>
                )}
                {!isChannelOwner && (
                  <div className="channel-dropdown-empty">No options available</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="nav-actions">
        {showSubChatToggle && !isNavPinned && (
          <button
            className="topbar-icon-btn topbar-icon-btn--indicator-wrap"
            onClick={openSubChatsPane}
            aria-label="Open sub-chats"
            title="Sub-chats"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 4h12M3 8h8M3 12h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            {hasUnreadSubChats && <span className="topbar-unread-dot" aria-hidden="true" />}
          </button>
        )}

        {showDetailToggle && !isDetailPinned && (
          <button
            className={`topbar-icon-btn${detailPaneOpen ? ' topbar-icon-btn--active' : ''}`}
            onClick={toggleDetailPane}
            aria-label="Toggle details panel"
            title="Toggle details panel"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M12 2v14" stroke="currentColor" strokeWidth="1.4"/>
            </svg>
          </button>
        )}

        <div className="profile-menu-wrap" ref={profileMenuRef}>
          <button
            className="profile-trigger"
            onClick={() => setProfileMenuOpen(o => !o)}
            aria-haspopup="true"
            aria-expanded={profileMenuOpen}
          >
            <div className="profile-avatar-sm" style={{ background: avatarColor(user.id) }}>
              {displayName.charAt(0).toUpperCase()}
            </div>
            <span className="profile-trigger-name">{displayName}</span>
            <span className="profile-trigger-caret">{profileMenuOpen ? '▲' : '▼'}</span>
          </button>

          {profileMenuOpen && (
            <div className="profile-dropdown" role="menu">
              <div className="profile-dropdown-header">
                <div className="profile-dropdown-name">{displayName}</div>
                {profile?.username && (
                  <div className="profile-dropdown-handle">@{profile.username}</div>
                )}
              </div>
              <div className="profile-dropdown-divider" />
              <button
                className="profile-dropdown-item danger"
                role="menuitem"
                onClick={() => { setProfileMenuOpen(false); onSignOut(); }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
